import { createSlice } from "@reduxjs/toolkit";
import {
  createErrorReportingAsyncThunk,
  serverRejectionMessage,
  ServerRejection,
} from "../../helpers";
import type { Dictionary } from "lodash";
import axios, { getData } from "../../../axios";
import type { AxiosResponse } from "axios";
import {
  BaseEquipment,
  Equipment,
  decodeEquipment,
  decodeEquipments,
  decodeEquipmentActiveRentals,
  UpdateEquipment,
  CreateEquipmentJob,
  EquipmentJobKind,
  EquipmentJobResult,
  decodeEquipmentJobResult,
} from "./types";
import keyBy from "lodash/keyBy";
import { notifyL } from "../../notifications";
import { DUPLICATE_EQUIPMENT_NUMBER } from "dora-shared";
import { updateEquipmentNoteT } from "dora-contracts";

const prefix = "data/equipment";

type State = {
  entities: Dictionary<Equipment>;
  ids: string[];
  loading: boolean;
  // Unit id -> number of the active rental it sits on. The server won't
  // archive those units, so the equipment list disables archiving for them.
  activeRentalNumbers: Dictionary<string>;
};

const initialState: State = {
  entities: {},
  ids: [],
  loading: false,
  activeRentalNumbers: {},
};

export const loadEquipment = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (): Promise<Equipment[]> => {
    return await axios
      .get("/api/equipment")
      .then(getData)
      .then(decodeEquipments);
  }
);

export const loadEquipmentActiveRentals = createErrorReportingAsyncThunk(
  `${prefix}/loadActiveRentals`,
  async () =>
    await axios
      .get("/api/equipment/active-rentals")
      .then(getData)
      .then(decodeEquipmentActiveRentals)
);

// The unit number is unique per org, so the server answers 409
// DUPLICATE_EQUIPMENT_NUMBER when adding or renumbering a unit to a taken
// number. That refusal is aimed at the user: it gets its own translated toast
// and becomes the rejection value, which leaves the dialog open and keeps it
// out of Sentry. Everything else is rethrown untouched, so
// createErrorReportingAsyncThunk reports it exactly once and classifies it
// first (a dropped connection or a 401 is not an "unexpected error" toast).
const saveEquipment = async (
  request: () => Promise<AxiosResponse>,
  thunkApi: {
    dispatch: (action: any) => unknown;
    rejectWithValue: (value: string) => any;
  }
) => {
  try {
    const equipment = await request().then(getData).then(decodeEquipment);
    thunkApi.dispatch(
      notifyL({
        namespace: "notifications",
        key: "equipmentSaved",
        type: "success",
      })
    );
    return equipment;
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (
      rejection?.kind === "code" &&
      rejection.code === DUPLICATE_EQUIPMENT_NUMBER
    ) {
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "errorSavingEquipmentDuplicateNumber",
          type: "error",
        })
      );
      return thunkApi.rejectWithValue(DUPLICATE_EQUIPMENT_NUMBER);
    }
    throw err;
  }
};

export const addEquipment = createErrorReportingAsyncThunk<
  Equipment,
  BaseEquipment,
  { rejectValue: string }
>(`${prefix}/add`, (values, thunkApi) =>
  saveEquipment(() => axios.post("/api/equipment", values), thunkApi)
);

export const updateEquipment = createErrorReportingAsyncThunk<
  Equipment,
  UpdateEquipment,
  { rejectValue: string }
>(`${prefix}/update`, (equipment, thunkApi) =>
  saveEquipment(
    () => axios.put(`/api/equipment/${equipment.id}`, equipment),
    thunkApi
  )
);

// Writes the note on one unit and nothing else about it. No success toast:
// the site job dialog saves several of these on its way to creating the job,
// and says itself when one of them fails.
export const updateEquipmentNote = createErrorReportingAsyncThunk(
  `${prefix}/updateNote`,
  async ({ id, note }: { id: string; note: string | null }) =>
    await axios
      .patch(`/api/equipment/${id}/note`, updateEquipmentNoteT.encode({ note }))
      .then(getData)
      .then(decodeEquipment)
);

export const archiveEquipment = createErrorReportingAsyncThunk(
  `${prefix}/archive`,
  async (id: string) => {
    await axios.post(`/api/equipment/${id}/archive`);
  }
);

type CreateEquipmentJobArgs = {
  equipmentId: string;
  kind: EquipmentJobKind;
  date: string;
  note: string | null;
  // The receiving facility the unit is emptied at, or null when it is not
  // driven anywhere to be emptied. Set, the order gets a third stop and the
  // LAST delivery is what decides the unit's outcome.
  emptyingPlace: CreateEquipmentJob["emptyingPlace"];
  // Who the unit is driven out to and where it is put. Both are needed for
  // DELIVER and null for the other kinds, which read them off the rental the
  // unit is already on.
  clientId: string | null;
  deliveryPlace: CreateEquipmentJob["deliveryPlace"];
};

// Creates a "Tøm" (EMPTY) or "Retur" (END_RENTAL) transport order for a unit
// that is with a customer. The server answers 400 { message } when the unit is
// not eligible (not with a customer, bulk, no coordinates, type cannot be
// emptied); that refusal is the rejection payload so the dialog can decide what
// to show. The equipment list itself is not touched here: the unit's status
// only changes when the driver completes the order.
export const createEquipmentJob = createErrorReportingAsyncThunk<
  EquipmentJobResult,
  CreateEquipmentJobArgs,
  { rejectValue: ServerRejection }
>(
  `${prefix}/create-job`,
  async (
    { equipmentId, kind, date, note, emptyingPlace, clientId, deliveryPlace },
    thunkApi
  ) => {
    const body: CreateEquipmentJob = {
      kind,
      date,
      note,
      emptyingPlace,
      clientId,
      deliveryPlace,
    };
    try {
      const result = await axios
        .post(`/api/equipment/${equipmentId}/jobs`, body)
        .then(getData)
        .then(decodeEquipmentJobResult);
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "equipmentJobCreated",
          type: "success",
        })
      );
      return result;
    } catch (err) {
      const rejection = serverRejectionMessage(err);
      if (rejection) {
        return thunkApi.rejectWithValue(rejection);
      }
      throw err;
    }
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadEquipment.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadEquipment.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loadEquipment.fulfilled, (state, action) => {
        state.loading = false;
        const equipment = action.payload;
        state.entities = keyBy(equipment, "id");
        state.ids = equipment.map((x) => x.id);
      })
      .addCase(loadEquipmentActiveRentals.fulfilled, (state, action) => {
        state.activeRentalNumbers = Object.fromEntries(
          action.payload.map((r) => [r.equipmentId, r.rentalNumber])
        );
      })
      .addCase(addEquipment.fulfilled, (state, action) => {
        const equipment = action.payload;
        state.ids.unshift(equipment.id);
        state.entities[equipment.id] = equipment;
      })
      .addCase(updateEquipment.fulfilled, (state, action) => {
        const equipment = action.payload;
        state.entities[equipment.id] = equipment;
      })
      .addCase(updateEquipmentNote.fulfilled, (state, action) => {
        const equipment = action.payload;
        if (state.entities[equipment.id])
          state.entities[equipment.id] = equipment;
      })
      .addCase(archiveEquipment.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.entities[id];
        state.ids = state.ids.filter((x) => x !== id);
      });
  },
});

export default slice.reducer;
